/**
 * RankingSection - Ranking section for scroll wizard
 */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTournament } from '../../contexts/TournamentContext';
import '../../styles/ranking.css';

const RankingSection = () => {
  const { players, schedule, recalculateRanking } = useTournament();
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  // Recalcule le classement quand le schedule change (chargement Firebase après F5)
  // et quand la page reprend le focus (retour sur l'onglet, etc.)
  useEffect(() => {
    const recalculate = () => {
      if (schedule && schedule.rounds && schedule.rounds.length > 0) {
        recalculateRanking(schedule);
      }
    };

    recalculate();

    window.addEventListener('focus', recalculate);
    return () => window.removeEventListener('focus', recalculate);
  }, [schedule, recalculateRanking]);

  const sortedPlayers = [...players].sort((a, b) => {
    if (b.points !== a.points) return b.points - a.points;
    if (b.setsWon !== a.setsWon) return b.setsWon - a.setsWon;
    if (b.gamesWon !== a.gamesWon) return b.gamesWon - a.gamesWon;
    return a.gamesLost - b.gamesLost;
  });

  return (
    <div className="section-inner">
      <div className="section-header">
        <h1>
          <i className="bi bi-trophy-fill me-3"></i>
          Classement
        </h1>
      </div>

      <div className="section-body">
        <div className="ranking-table-wrapper">
          <table className="table ranking-table">
            <thead>
              <tr>
                <th>Rang</th>
                <th>Joueur</th>
                <th className="text-center" title="Position (Gauche / Droite)">Pos.</th>
                <th className="text-center fw-bold" title="Points">Pts</th>
                <th className="text-center" title="Jeux gagnés">Jeu+</th>
                <th className="text-center" title="Jeux perdus">Jeu-</th>
                <th className="text-center" title="Victoires">V</th>
                <th className="text-center" title="Nuls">N</th>
                <th className="text-center" title="Défaites">D</th>
              </tr>
            </thead>
            <tbody>
              {sortedPlayers.map((player, index) => (
                <tr key={player.id}>
                  <td className="fw-bold">{index + 1}</td>
                  <td
                    className="player-name-clickable"
                    onClick={() => setSelectedPlayer({ ...player, rank: index + 1 })}
                  >{player.name}</td>
                  <td className="text-center">{player.position === 'left' ? 'G' : 'D'}</td>
                  <td className="text-center fw-bold text-primary">{player.points || 0}</td>
                  <td className="text-center">{player.gamesWon || 0}</td>
                  <td className="text-center">{player.gamesLost || 0}</td>
                  <td className="text-center text-success">{player.wins || 0}</td>
                  <td className="text-center text-secondary">{player.draws || 0}</td>
                  <td className="text-center text-danger">{player.losses || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Player Stats Modal */}
        {selectedPlayer && createPortal(
          <>
            <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} onClick={() => setSelectedPlayer(null)}></div>
            <div className="modal fade show d-block player-stats-modal" tabIndex="-1" role="dialog" style={{ zIndex: 1050 }}>
              <div className="modal-dialog modal-dialog-centered" role="document">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">
                      <i className="bi bi-person-circle me-2"></i>
                      {selectedPlayer.name}
                      <span className="ms-2 badge bg-secondary">#{selectedPlayer.rank}</span>
                    </h5>
                    <button type="button" className="btn-close" onClick={() => setSelectedPlayer(null)} aria-label="Fermer"></button>
                  </div>
                  <div className="modal-body">
                    <div className="player-stats-grid">
                      <div className="player-stat-item">
                        <div className="player-stat-value text-primary">{selectedPlayer.points || 0}</div>
                        <div className="player-stat-label">Points</div>
                      </div>
                      <div className="player-stat-item">
                        <div className="player-stat-value">{selectedPlayer.position === 'left' ? 'Gauche' : 'Droite'}</div>
                        <div className="player-stat-label">Position</div>
                      </div>
                      <div className="player-stat-item">
                        <div className="player-stat-value text-success">{selectedPlayer.wins || 0}</div>
                        <div className="player-stat-label">Victoires</div>
                      </div>
                      <div className="player-stat-item">
                        <div className="player-stat-value text-secondary">{selectedPlayer.draws || 0}</div>
                        <div className="player-stat-label">Nuls</div>
                      </div>
                      <div className="player-stat-item">
                        <div className="player-stat-value text-danger">{selectedPlayer.losses || 0}</div>
                        <div className="player-stat-label">Défaites</div>
                      </div>
                      <div className="player-stat-item">
                        <div className="player-stat-value">{selectedPlayer.played || 0}</div>
                        <div className="player-stat-label">Matchs joués</div>
                      </div>
                      <div className="player-stat-item">
                        <div className="player-stat-value text-success">{selectedPlayer.gamesWon || 0}</div>
                        <div className="player-stat-label">Jeux gagnés</div>
                      </div>
                      <div className="player-stat-item">
                        <div className="player-stat-value text-danger">{selectedPlayer.gamesLost || 0}</div>
                        <div className="player-stat-label">Jeux perdus</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>,
          document.body
        )}

        <div className="section-end mt-5">
          <button
            type="button"
            className="btn btn-lg btn-generate"
            title="Aller à la phase finale"
            onClick={() => {
              const finalSection = document.getElementById('section-final');
              if (finalSection) {
                finalSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }}
          >
            <i className="bi bi-flag-fill me-2"></i>
            Phase finale
          </button>
        </div>
      </div>
    </div>
  );
};

export default RankingSection;
